// Data feedback ("Report data issue") API client — BUILD-ORDER-data-feedback.
//
// Own axios instance, isolated from the shared services/api.js (small PR
// surface, no risk to the shared client) — mirrors services/agentRadarService.js
// and services/clubRadarService.js. Backend contract
// (dashboard/data_feedback_views.py, totw/v1-style versioned paths):
//   POST /dashboard/data-feedback/v1/reports/
//        { entity_type: "player"|"club", wyscout_id, category, comment? }
//        { entity_type: "coach", ts_coach_id, category, comment? }
//   GET  /dashboard/data-feedback/v1/reports/status/?entity_type=&wyscout_id=
//        (coach lane: ?entity_type=coach&ts_coach_id=)
//        -> { reported_today, categories }
// The response never carries a TheSports id — that reference lives only in
// the daily digest mail to TheSports, never in the UI.
import axios from "axios";

const API_BASE_URL = process.env.REACT_APP_API_ENDPOINT;

const authApi = axios.create({ baseURL: API_BASE_URL, withCredentials: true });

// Same Bearer-token attachment the radar / scout / club-network clients use.
authApi.interceptors.request.use((config) => {
  try {
    const tokens = JSON.parse(localStorage.getItem("tokens") || "null");
    if (tokens?.access) {
      config.headers = config.headers || {};
      config.headers.Authorization = `Bearer ${tokens.access}`;
    }
  } catch (e) {
    /* no token — request proceeds and the server returns 401 */
  }
  return config;
});

const fail = (error) => ({
  success: false,
  status: error?.response?.status,
  error:
    error?.response?.data?.detail ||
    error?.response?.data?.message ||
    error?.response?.data?.error ||
    (typeof error?.response?.data === "object"
      ? Object.values(error.response.data)[0]
      : null) ||
    "Something went wrong. Please try again.",
});

const isCancel = (error) =>
  error?.name === "CanceledError" || error?.name === "AbortError";

const BASE = "/dashboard/data-feedback/v1";

// One click = one category. Values are the backend enum verbatim; labels are
// the English UI copy (translated by the DOM scanner via the
// report-data-issue.json bundle).
export const DATA_FEEDBACK_CATEGORIES = [
  { value: "market_value", label: "Wrong market value" },
  { value: "position", label: "Wrong position" },
  { value: "club_squad", label: "Wrong club or squad" },
  { value: "contract", label: "Wrong contract data" },
  { value: "name_photo", label: "Wrong name or photo" },
  { value: "other", label: "Other" },
];

export const dataFeedbackService = {
  // "Has this user already reported this entity today?" — status probe for
  // the modal (drives the "already reported" state, no double post).
  getStatus: async ({ entityType, wyscoutId, tsCoachId }, signal) => {
    try {
      // Coaches are addressed by their TheSports id (string), players/clubs
      // by the numeric wyscout id — never both.
      const idParams = tsCoachId
        ? { ts_coach_id: tsCoachId }
        : { wyscout_id: wyscoutId };
      const res = await authApi.get(`${BASE}/reports/status/`, {
        params: { entity_type: entityType, ...idParams },
        signal,
      });
      return { success: true, data: res.data };
    } catch (error) {
      if (isCancel(error)) throw error;
      return fail(error);
    }
  },

  reportIssue: async ({ entityType, wyscoutId, tsCoachId, category, comment }) => {
    try {
      const body = tsCoachId
        ? { entity_type: entityType, ts_coach_id: tsCoachId, category }
        : { entity_type: entityType, wyscout_id: wyscoutId, category };
      if (comment && comment.trim()) body.comment = comment.trim();
      const res = await authApi.post(`${BASE}/reports/`, body);
      return { success: true, data: res.data };
    } catch (error) {
      return fail(error);
    }
  },
};

export default dataFeedbackService;
